import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Search } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const Navigation = ({ currentSection = 'Christian Fiebig', searchValue = '', onSearchChange }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  const isMethodologyPage = location.pathname === '/methodology'
  const isAboutPage = location.pathname === '/about'
  const isHome = location.pathname === '/'

  const closeMobileMenu = () => setMobileMenuOpen(false)

  const scrollToProjects = (e) => {
    e.preventDefault()
    closeMobileMenu()
    if (!isHome) {
      window.location.href = '/#projects'
      return
    }
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 px-0 py-6 backdrop-blur-nav bg-white/80 border-b border-border/50"
    >
      <div className="w-full max-w-[1400px] mx-auto flex flex-wrap justify-between items-center gap-4 px-[5%]">
        <div className="flex-1 min-w-[140px]">
          <div className="text-[1.75rem] leading-[1.75rem] font-semibold text-foreground truncate">
            {currentSection}
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-5 flex-1 justify-center">
          <button
            type="button"
            onClick={scrollToProjects}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors bg-transparent border-0 cursor-pointer p-0"
          >
            Projects
          </button>
          <Link
            to="/methodology"
            className={`text-sm transition-colors ${
              isMethodologyPage ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Methodology
          </Link>
          <Link
            to="/about"
            className={`text-sm transition-colors ${
              isAboutPage ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            About
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-2 flex-1 justify-end min-w-0">
          <div className="relative flex items-center max-w-[200px] lg:max-w-[240px]">
            <Search className="absolute left-2.5 h-4 w-4 text-muted-foreground pointer-events-none" aria-hidden />
            <input
              type="search"
              placeholder="Search projects…"
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-full rounded-full border border-border bg-background/80 py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Search projects"
            />
          </div>
          <Link
            to="/hub/login"
            className={cn(buttonVariants({ variant: 'outline', size: 'default' }), 'shrink-0 no-underline')}
          >
            Login
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden p-2 rounded-full hover:bg-secondary transition-colors"
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            {mobileMenuOpen ? <path d="M6 18L18 6M6 6l12 12" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden overflow-hidden border-t border-border/50 bg-background/95 backdrop-blur-nav"
          >
            <div className="px-[5%] py-4 space-y-3">
              <button
                type="button"
                onClick={scrollToProjects}
                className="block w-full text-left text-sm py-2 text-muted-foreground hover:text-foreground"
              >
                Projects
              </button>
              <Link
                to="/methodology"
                onClick={closeMobileMenu}
                className={`block text-sm py-2 ${isMethodologyPage ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
              >
                Methodology
              </Link>
              <Link
                to="/about"
                onClick={closeMobileMenu}
                className={`block text-sm py-2 ${isAboutPage ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
              >
                About
              </Link>
              <div className="relative flex items-center pt-2">
                <Search className="absolute left-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input
                  type="search"
                  placeholder="Search…"
                  value={searchValue}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  className="w-full rounded-full border border-border py-2 pl-9 pr-3 text-sm"
                />
              </div>
              <Link
                to="/hub/login"
                onClick={closeMobileMenu}
                className={cn(buttonVariants({ variant: 'outline' }), 'w-full justify-center no-underline')}
              >
                Login
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}

export default Navigation
